import { useRef, useState, useEffect, ReactNode } from "react";

interface LazyWhenVisibleProps {
  children: ReactNode;
  rootMargin?: string;
  fallback?: ReactNode;
}

/**
 * Defers rendering of children until the placeholder enters the viewport.
 * This prevents lazy-loaded heavy components (e.g. framer-motion) from
 * loading during initial page render when they are below the fold.
 */
const LazyWhenVisible = ({ children, rootMargin = "200px", fallback = null }: LazyWhenVisibleProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  if (isVisible) return <>{children}</>;

  return <div ref={ref}>{fallback}</div>;
};

export default LazyWhenVisible;
